import React, { useState } from 'react';
import { Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import AtomButton from '@/components/atoms/Button';
import AtomTextField from '@/components/atoms/TextField';
import { AppDispatch } from '@/store';
import { studiesLoading, studiesError } from '@/store/studies/selectors';
import { studies } from '@/store/studies/thunks';
import styles from './create-study.module.css';

interface CreateStudyDialogContentProps {
  onClose: () => void;
  onSuccess?: () => void;
}

interface StudyFormData {
  name: string;
  description: string;
  year: number;
  revenue: number;
  units_sold: number;
  technology_group: string;
  automation_type: string;
  companies: any[];
  segments: any[];
}

const CreateStudyDialogContent: React.FC<CreateStudyDialogContentProps> = ({
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const isCreatingStudy = useSelector(studiesLoading);
  const createStudyError = useSelector(studiesError);
  const currentUser = useSelector((state: any) => state.auth?.user);

  // State
  const [formData, setFormData] = useState<StudyFormData>({
    name: '',
    description: '',
    year: new Date().getFullYear(),
    revenue: 0,
    units_sold: 0,
    technology_group: 'systems',
    automation_type: 'hybrid',
    companies: [],
    segments: [],
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Handlers
  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Study name is required';
    }

    if (formData.year < 1900 || formData.year > 2100) {
      errors.year = 'Please enter a valid year';
    }

    if (formData.revenue < 0) {
      errors.revenue = 'Revenue cannot be negative';
    }

    if (formData.units_sold < 0) {
      errors.units_sold = 'Units sold cannot be negative';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const studyData = {
        user_id: currentUser?.id || 'default-user-id',
        name: formData.name,
        description: formData.description || 'No description provided',
        year: formData.year,
        revenue: formData.revenue,
        units_sold: formData.units_sold,
        technology_group: formData.technology_group,
        automation_type: formData.automation_type,
        companies: formData.companies,
        segments: formData.segments,
        is_legacy_study: false,
      };

      await dispatch(studies.createStudy(studyData));

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close dialog
      onClose();
    } catch (error) {
      console.error('Error creating study:', error);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <div className={styles.formRow}>
          <AtomTextField
            label="Study Name"
            value={formData.name}
            onChange={e => handleFormChange('name', e.target.value)}
            error={!!formErrors.name}
            helperText={formErrors.name}
            required
            placeholder="Enter study name"
            fullWidth
          />
        </div>

        <div className={styles.formRow}>
          <AtomTextField
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={e => handleFormChange('description', e.target.value)}
            placeholder="Enter study description (optional)"
            fullWidth
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <AtomTextField
              label="Year"
              type="number"
              value={formData.year}
              onChange={e => handleFormChange('year', Number(e.target.value))}
              error={!!formErrors.year}
              helperText={formErrors.year}
              InputProps={{
                inputProps: { min: 1900, max: 2100 },
              }}
            />
          </div>
          <div className={styles.formGroup}>
            <AtomTextField
              label="Revenue"
              type="number"
              value={formData.revenue}
              onChange={e => handleFormChange('revenue', Number(e.target.value))}
              error={!!formErrors.revenue}
              helperText={formErrors.revenue}
              placeholder="Enter initial revenue"
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <AtomTextField
              label="Units Sold"
              type="number"
              value={formData.units_sold}
              onChange={e => handleFormChange('units_sold', Number(e.target.value))}
              error={!!formErrors.units_sold}
              helperText={formErrors.units_sold}
              placeholder="Enter initial units sold"
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </div>
          <div className={styles.formGroup}>
            <AtomTextField
              select
              label="Technology Group"
              value={formData.technology_group}
              onChange={e => handleFormChange('technology_group', e.target.value)}
            >
              <option value="systems">Systems</option>
              <option value="software">Software</option>
              <option value="hardware">Hardware</option>
              <option value="services">Services</option>
            </AtomTextField>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <AtomTextField
              select
              label="Automation Type"
              value={formData.automation_type}
              onChange={e => handleFormChange('automation_type', e.target.value)}
            >
              <option value="hybrid">Hybrid</option>
              <option value="manual">Manual</option>
              <option value="automated">Automated</option>
            </AtomTextField>
          </div>
          <div className={styles.formGroup}>{/* Empty div to maintain grid layout */}</div>
        </div>
      </div>

      {/* Error Display */}
      {createStudyError && (
        <div className={styles.errorMessage}>
          <p>Error creating study: {createStudyError}</p>
        </div>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <Stack direction="row" spacing={2}>
          <AtomButton variant="outlined" onClick={onClose} disabled={isCreatingStudy}>
            Cancel
          </AtomButton>
          <AtomButton variant="contained" onClick={handleSubmit} loading={isCreatingStudy}>
            Create Study
          </AtomButton>
        </Stack>
      </div>
    </div>
  );
};

export default CreateStudyDialogContent;
